import { useState } from "react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { 
  Settings, 
  Database, 
  RefreshCw, 
  Bell, 
  Shield, 
  User, 
  Key, 
  Save,
  CheckCircle,
  UserPlus,
  Mail,
  Users,
  Trash2,
  Send
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { useSelector } from "react-redux";
import BASE_URL from "@/constants";

// Team role mapping
const TEAM_ROLES = {
  'Planner': ['Planning Manager', 'Senior Planner', 'Planner', 'Planning Analyst'],
  'Appointment': ['Appointment Manager', 'Senior Scheduler', 'Scheduler', 'Appointment Coordinator'],
  'Sales Order': ['SO Manager', 'Senior SO Executive', 'SO Executive', 'SO Analyst'],
  'Courier': ['Logistics Manager', 'Senior Courier Executive', 'Courier Executive', 'Delivery Coordinator'],
  'Invoicing': ['Finance Manager', 'Senior Accountant', 'Accountant', 'Invoice Specialist'],
  'Warehouse': ['Warehouse Manager', 'Senior Warehouse Executive', 'Warehouse Executive', 'Warehouse Assistant']
};

interface TeamInvite {
  id: string;
  email: string;
  team: string;
  role: string;
  status: 'pending' | 'sent' | 'failed';
  sentAt?: Date;
}

const SettingsPage = () => {
  const { toast } = useToast();
    const userName = useSelector((state: any) => state.user.name);
    const userEmail = useSelector((state: any) => state.user.email);
    let nameArr = userName.split(" ");
    let firstName = nameArr[0]
    let lastName = nameArr[1]
  const [sapConnected, setSapConnected] = useState(true);
  const role = useSelector((state: any) => state.user.role);
  // General Settings State
  const [saving, setSaving] = useState(false);
    const email = useSelector((state: any) => state.user.email);
  // Admin Profile State
  const [adminProfile, setAdminProfile] = useState({
    firstName: 'First name',
    lastName: 'Last name',
  });
  
  // Team Invitation State
  const [inviteForm, setInviteForm] = useState({
    email: '',
    team: '',
    role: ''
  });
  const [invitedMembers, setInvitedMembers] = useState<TeamInvite[]>([
    {
      id: '1',
      email: 'sarah.johnson@nuvoretail.com',
      team: 'Planner',
      role: 'Planning Manager',
      status: 'sent',
      sentAt: new Date(Date.now() - 2 * 60 * 60 * 1000) // 2 hours ago
    },
    {
      id: '2',
      email: 'mike.davis@nuvoretail.com',
      team: 'Warehouse',
      role: 'Warehouse Manager',
      status: 'sent',
      sentAt: new Date(Date.now() - 5 * 60 * 60 * 1000) // 5 hours ago
    }
  ]);
  const [sendingInvite, setSendingInvite] = useState(false);
  

  const[existingPassword, setExistingPassword] = useState("")
  const[newPassword, setNewPassword] = useState("")
  const[newPassword2, setNewPassword2] = useState("")
const [settingPassword, setSettingPassword] = useState(false);

const changePassword = async () => {
  if (!existingPassword || !newPassword || !newPassword2) {
    return;
  }
  setSettingPassword(true)

  if (newPassword.length < 8) {
    toast({
      title: "Password too short",
      description: "New password must be at least 8 characters long",
      variant: "destructive",
    });
     setSettingPassword(false)
    return;
  }

  if (newPassword !== newPassword2) {
    toast({
      title: "Password mismatch",
      description: "New passwords do not match",
      variant: "destructive",
    });
     setSettingPassword(false)
    return;
  }

  try {
    // Example: Adjust URL as needed
    const response = await axios.post(BASE_URL +"/users/reset-password", {
      email: userEmail, // ideally get this from auth context or props
      existingPassword,
      newPassword,
    });

    toast({
      title: "Password updated",
      description: response.data.message || "Password changed successfully",
    });

    // Optional: clear fields
    setExistingPassword("");
    setNewPassword("");
    setNewPassword2("");
  } catch (error: any) {
    toast({
      title: "Error",
      description: error?.response?.data?.message || "Something went wrong",
      variant: "destructive",
    });
  }
  finally {
    setSettingPassword(false)
  }
};

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast({
        title: "Settings saved",
        description: "Your settings have been successfully saved",
      });
    }, 1000);
  };
  
  const handleSync = () => {
    toast({
      title: "System Synchronized",
      description: "SAP system synchronization completed successfully",
    });
  };
  
  const handleDisconnect = () => {
    toast({
      description: "Are you sure you want to disconnect from SAP? This will affect data synchronization.",
      action: (
        <Button 
          variant="destructive" 
          onClick={() => {
            setSapConnected(false);
            toast({
              title: "SAP Disconnected",
              description: "You have been disconnected from SAP",
            });
          }}
        >
          Disconnect
        </Button>
      ),
    });
  };
  
  const handleConnect = () => {
    setSapConnected(true);
    toast({
      title: "SAP Connected",
      description: "You have been connected to SAP successfully",
    });
  };

  // Admin Profile Handlers
  const handleAdminProfileSave = () => {
    return;
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast({
        title: "Profile Updated",
        description: "Admin profile has been successfully updated",
      });
    }, 1000);
  };

  // Team Invitation Handlers
  const handleTeamChange = (team: string) => {
    setInviteForm(prev => ({
      ...prev,
      team,
      role: '' // Reset role when team changes
    }));
  };

  const handleSendInvite = async() => {
    setSendingInvite(true)
    if (!inviteForm.email || !inviteForm.team) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields before sending invitation",
        variant: "destructive"
      });
      return;
    }

        const payload = {
        email:inviteForm.email,
        teamName: inviteForm.team,
        companyName: "Novestar"
      }
  
      // return
    try {
      const response = await axios.post(BASE_URL+'/users/invite', payload);
          setTimeout(() => {
      const newInvite: TeamInvite = {
        id: Date.now().toString(),
        email: inviteForm.email,
        team: inviteForm.team,
        role: inviteForm.role,
        status: 'sent',
        sentAt: new Date()
      };
      
      setInvitedMembers(prev => [newInvite, ...prev]);
      setInviteForm({ email: '', team: '', role: '' });
      setSendingInvite(false);
      
        toast({
        title: "Invitation Sent!",
        description: `Invitation email sent to ${inviteForm.email} successfully`,
      });
    }, 2000);

      
    } catch (error) {
   
          toast({
         title: "Couldn't send Invite",
        description: "Internal Server Error",
        variant: "destructive"
      });
      
    } finally {
      

    }
    
    
    // Simulate sending email invitation

  };

  const handleRemoveInvite = (id: string) => {
    setInvitedMembers(prev => prev.filter(invite => invite.id !== id));
    toast({
      title: "Invitation Removed",
      description: "Team member invitation has been removed",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent': return <Badge variant="secondary" className="text-green-700 bg-green-100">Sent</Badge>;
      case 'pending': return <Badge variant="outline">Pending</Badge>;
      case 'failed': return <Badge variant="destructive">Failed</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  return (
    <main className="p-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-semibold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-600">Manage your application preferences and integrations</p>
      </div>
      
      <Tabs defaultValue="general">
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="w-full sm:w-64">
            <TabsList className="flex flex-col h-auto w-full gap-1 bg-transparent">
              <TabsTrigger 
                value="general" 
                className="justify-start w-full px-3 py-2 bg-white hover:bg-gray-100 data-[state=active]:bg-primary/10"
              >
                <Settings className="w-4 h-4 mr-2" />
                General
              </TabsTrigger>
              <TabsTrigger 
                value="integrations" 
                className="justify-start w-full px-3 py-2 bg-white hover:bg-gray-100 data-[state=active]:bg-primary/10"
              >
                <Database className="w-4 h-4 mr-2" />
                Integrations
              </TabsTrigger>
              <TabsTrigger 
                value="notifications" 
                className="justify-start w-full px-3 py-2 bg-white hover:bg-gray-100 data-[state=active]:bg-primary/10"
              >
                <Bell className="w-4 h-4 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger 
                value="security" 
                className="justify-start w-full px-3 py-2 bg-white hover:bg-gray-100 data-[state=active]:bg-primary/10"
              >
                <Shield className="w-4 h-4 mr-2" />
                Security
              </TabsTrigger>
              <TabsTrigger 
                value="user" 
                className="justify-start w-full px-3 py-2 bg-white hover:bg-gray-100 data-[state=active]:bg-primary/10"
              >
                <User className="w-4 h-4 mr-2" />
                User Profile
              </TabsTrigger>
            </TabsList>
          </div>
          
          <div className="flex-1">
            <TabsContent value="general" className="m-0">
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                  <CardDescription>Manage your application preferences and display settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">Compact Mode</h3>
                        <p className="text-sm text-gray-500">Display more information with a compact layout</p>
                      </div>
                      <Switch disabled defaultChecked />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">Auto-refresh Dashboard</h3>
                        <p className="text-sm text-gray-500">Automatically refresh dashboard data</p>
                      </div>
                      <Switch disabled defaultChecked />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">Use 24-hour Format</h3>
                        <p className="text-sm text-gray-500">Display time in 24-hour format</p>
                      </div>
                      <Switch disabled defaultChecked />
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label htmlFor="language">Language</Label>
                      <Select defaultValue="en">
                        <SelectTrigger>
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          {/* <SelectItem value="es">Spanish</SelectItem>
                          <SelectItem value="fr">French</SelectItem>
                          <SelectItem value="de">German</SelectItem>
                          <SelectItem value="zh">Chinese</SelectItem> */}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button onClick={handleSave} 
                  // disabled={saving}
                  disabled
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>
            
            <TabsContent value="integrations" className="m-0">
              <Card>
                <CardHeader>
                  <CardTitle>System Integrations</CardTitle>
                  <CardDescription>Connect and manage your SAP and other system integrations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">SAP Integration</h3>
                        <p className="text-sm text-gray-500">
                          {sapConnected 
                            ? "Connected to SAP ERP System" 
                            : "Not connected to SAP ERP System"
                          }
                        </p>
                        {sapConnected && (
                          <div className="flex items-center text-sm text-success mt-1">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Last synchronized: Today at 09:45 AM
                          </div>
                        )}
                      </div>
                      {sapConnected ? (
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={handleSync}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Sync
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-destructive" 
                            onClick={handleDisconnect}
                          >
                            Disconnect
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleConnect}
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">SAP Connection Settings</h3>
                      <div className="grid gap-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="sap-host">SAP Host</Label>
                            <Input
                              id="sap-host"
                              placeholder="sap.example.com"
                              defaultValue="erp.nuvoretail.com"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="sap-port">Port</Label>
                            <Input
                              id="sap-port"
                              placeholder="3000"
                              defaultValue="3000"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="sap-client">SAP Client</Label>
                            <Input
                              id="sap-client"
                              placeholder="100"
                              defaultValue="100"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="sap-language">SAP Language</Label>
                            <Input
                              id="sap-language"
                              placeholder="EN"
                              defaultValue="EN"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="auth-method">Authentication Method</Label>
                          <Select defaultValue="basic">
                            <SelectTrigger>
                              <SelectValue placeholder="Select authentication method" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="basic">Basic Authentication</SelectItem>
                              <SelectItem value="saml">SAML</SelectItem>
                              <SelectItem value="oauth">OAuth</SelectItem>
                              <SelectItem value="sso">Single Sign-On</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Synchronization Settings</h3>
                      <div className="space-y-2">
                        <Label htmlFor="sync-interval">Sync Interval</Label>
                        <Select defaultValue="15">
                          <SelectTrigger>
                            <SelectValue placeholder="Select interval" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5">Every 5 minutes</SelectItem>
                            <SelectItem value="15">Every 15 minutes</SelectItem>
                            <SelectItem value="30">Every 30 minutes</SelectItem>
                            <SelectItem value="60">Every hour</SelectItem>
                            <SelectItem value="daily">Once daily</SelectItem>
                            <SelectItem value="manual">Manual only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium">Auto-sync on PO Creation</h3>
                          <p className="text-sm text-gray-500">Automatically sync with SAP when a new PO is created</p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium">Auto-sync on Inventory Update</h3>
                          <p className="text-sm text-gray-500">Automatically sync with SAP when inventory is updated</p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>
            
            <TabsContent value="notifications" className="m-0">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Settings</CardTitle>
                  <CardDescription>Configure how and when you receive notifications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">Email Notifications</h3>
                        <p className="text-sm text-gray-500">Receive notifications via email</p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">In-App Notifications</h3>
                        <p className="text-sm text-gray-500">Receive notifications within the application</p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">PO Validation Notifications</h3>
                        <p className="text-sm text-gray-500">Notify when a PO requires validation</p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">Inventory Alert Notifications</h3>
                        <p className="text-sm text-gray-500">Notify when inventory reaches critical levels</p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium">Daily Summary</h3>
                        <p className="text-sm text-gray-500">Receive a daily summary of activities</p>
                      </div>
                      <Switch />
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>
            
            <TabsContent value="security" className="m-0">
              <Card>
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                  <CardDescription>Manage your account security settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium">Change Password</h3>
                      <div className="space-y-2">
                        <Label htmlFor="current-password">Current Password</Label>
                        <Input
                        value={existingPassword}
                        onChange={(e)=>setExistingPassword(e.target.value)}
                          id="current-password"
                          type="password"
                          placeholder="Enter your current password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-password">New Password</Label>
                        <Input
                             value={newPassword}
                        onChange={(e)=>setNewPassword(e.target.value)}
                          id="new-password"
                          type="password"
                          placeholder="Enter your new password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirm New Password</Label>
                        <Input
                             value={newPassword2}
                        onChange={(e)=>setNewPassword2(e.target.value)}
                          id="confirm-password"
                          type="password"
                          placeholder="Confirm your new password"
                        />
                      </div>
                              <div className="flex justify-end">
                  <Button
  onClick={changePassword}
  disabled={
    !existingPassword ||
    !newPassword ||
    !newPassword2 ||
    newPassword.length < 8
    
  }
>
  {settingPassword ? (
    <>
      <Send className="mr-2 h-4 w-4 animate-pulse" />
      Setting Password...
    </>
  ) : (
    <>
      <Send className="mr-2 h-4 w-4" />
      Change Password
    </>
  )}
</Button>

                  </div>
                      {/* <Button   disabled={!existingPassword || newPassword || !newPassword2} onClick={changePassword} className="mt-2" variant="outline">Change Password</Button> */}
                    </div>
                    
                    {/* <Separator />
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium">Two-Factor Authentication</h3>
                          <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                        </div>
                        <Switch />
                      </div>
                      <Button className="mt-2" variant="outline">Setup 2FA</Button>
                    </div> */}
                    
                    {/* <Separator /> */}
                    
                    {/* <div className="space-y-2">
                      <h3 className="text-sm font-medium">API Keys</h3>
                      <p className="text-sm text-gray-500">Manage API keys for integration with other systems</p>
                      <div className="flex items-center justify-between bg-muted p-4 rounded-md">
                        <div>
                          <p className="text-sm font-medium">SAP Integration Key</p>
                          <p className="text-xs text-gray-500">Created on May 1, 2025</p>
                        </div>
                        <div className="flex items-center">
                          <Key className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span className="text-sm font-mono">•••••••••••••••••••••</span>
                        </div>
                      </div>
                      <Button className="mt-2" variant="outline">Generate New API Key</Button>
                    </div> */}
                  </div>
                </CardContent>
                {/* <CardFooter className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </CardFooter> */}
              </Card>
            </TabsContent>
            
            <TabsContent value="user" className="m-0 space-y-6">
              {/* 1. Admin Profile Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Admin Profile
                  </CardTitle>
                  <CardDescription>Set up your profile to personalize your experience</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin-first-name">First Name</Label>
                      <Input
                        id="admin-first-name"
                        value={firstName}
                        // disabled
                        // onChange={(e) => setAdminProfile(prev => ({ ...prev, firstName: e.target.value }))}
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin-last-name">Last Name</Label>
                      <Input
                        id="admin-last-name"
                        value={lastName}
                        // onChange={(e) => setAdminProfile(prev => ({ ...prev, lastName: e.target.value }))}
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-email">Email Address</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      value={email}
                      onChange={(e) => setAdminProfile(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Enter your email address"
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button disabled onClick={handleAdminProfileSave}>
                    {saving ? "Saving..." : "Save Profile"}
                    {saving && <Save className="ml-2 h-4 w-4 animate-spin" />}
                  </Button>
                </CardFooter>
              </Card>

              {/* 2. Invite Team Members Section */}

              {
                role==="Manager" &&  <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    Invite Team Members
                  </CardTitle>
                  <CardDescription>Send email invitations to team members to join the platform</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Invitation Form */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="invite-email">Email Address</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="example@nuvostar.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite-team">Team</Label>
                      <Select value={inviteForm.team} onValueChange={handleTeamChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Planning Team">Planner</SelectItem>
                          <SelectItem value="Appointment Team">Appointment</SelectItem>
                          <SelectItem value="Sales Order Team">Sales Order</SelectItem>
                          <SelectItem value="Courier Team">Courier</SelectItem>
                          <SelectItem value="Invoicing Team">Invoicing</SelectItem>
                          <SelectItem value="Warehouse Team">Warehouse</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* <div className="space-y-2">
                      <Label htmlFor="invite-role">Role</Label>
                      <Select 
                        value={inviteForm.role} 
                        onValueChange={(role) => setInviteForm(prev => ({ ...prev, role }))}
                        disabled={!inviteForm.team}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {inviteForm.team && TEAM_ROLES[inviteForm.team as keyof typeof TEAM_ROLES]?.map((role) => (
                            <SelectItem key={role} value={role}>{role}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div> */}
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={handleSendInvite} 
                      disabled={sendingInvite || !inviteForm.email || !inviteForm.team }
                    >
                      {sendingInvite ? (
                        <>
                          <Send className="mr-2 h-4 w-4 animate-pulse" />
                          Sending Invitation...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send Invitation
                        </>
                      )}
                    </Button>
                  </div>

                  <Separator />

                  {/* Invited Members List */}
                  {/* <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium">Invited Members</h3>
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {invitedMembers.length} invited
                      </Badge>
                    </div>
                    
                    {invitedMembers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Mail className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="font-medium">No invitations sent yet</p>
                        <p className="text-sm">Start by inviting your first team member above</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {invitedMembers.map((invite) => (
                          <div key={invite.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="font-medium text-gray-900">{invite.email}</div>
                                {getStatusBadge(invite.status)}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {invite.team}
                                </div>
                                <div>{invite.role}</div>
                                {invite.sentAt && (
                                  <div className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    Sent {invite.sentAt.toLocaleDateString()} at {invite.sentAt.toLocaleTimeString()}
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveInvite(invite.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div> */}
                </CardContent>
              </Card>
              }
             
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </main>
  );
};

export default SettingsPage;